/**
 * Units, currencies and the two rounding rules (plan §3.1, §6).
 *
 * - **Storage** rounding is applied once, at input (`roundForUnit` / `parseInputForUnit`): Percentage, Days,
 *   Hours and Decimal keep one decimal, Ratio two, Number is an integer, Currency is kept exact (to the
 *   six-decimal scale of the `numeric(18,6)` column, so engine and database hold the same number).
 * - **Display** rounding is one decimal everywhere (`formatValue`), with the currency code in front:
 *   `JOD 1,450.0`. Full precision is kept internally; nothing is rounded twice.
 * - A blank is never zero: `null` in, `null` (or `—`) out.
 */
export const UNITS = ['NUMBER', 'PERCENTAGE', 'CURRENCY', 'DAYS', 'HOURS', 'RATIO', 'DECIMAL', 'TEXT'];
export const CURRENCIES = ['JOD', 'USD', 'EUR', 'CNY', 'JPY'];
export const isUnit = (value) => typeof value === 'string' && UNITS.includes(value);
export const isCurrency = (value) => typeof value === 'string' && CURRENCIES.includes(value);
export const isNumericUnit = (unit) => unit !== 'TEXT';
/** Decimal places kept in storage per unit. */
export const STORAGE_DECIMALS = {
    NUMBER: 0,
    PERCENTAGE: 1,
    CURRENCY: 6,
    DAYS: 1,
    HOURS: 1,
    RATIO: 2,
    DECIMAL: 1,
};
export const DISPLAY_DECIMALS = 1;
/** Shown wherever a value is blank (missing input, ÷0, restricted source). */
export const BLANK_DISPLAY = '—';
/** A currency is required if and only if the unit is `CURRENCY`. */
export const currencyRuleError = (unit, currency) => {
    const hasCurrency = currency !== null && currency !== undefined;
    if (unit === 'CURRENCY') {
        return hasCurrency ? null : 'CURRENCY_REQUIRED';
    }
    return hasCurrency ? 'CURRENCY_NOT_ALLOWED' : null;
};
/**
 * Half-away-from-zero rounding to `decimals` places that is immune to binary noise (`1.005` → `1.01`, not `1`):
 * the scaled value is first cleaned to 15 significant digits, the precision a double actually carries.
 */
export const roundTo = (value, decimals) => {
    if (!Number.isFinite(value)) {
        throw new RangeError(`Cannot round a non-finite value: ${value}`);
    }
    const factor = 10 ** decimals;
    const scaled = Number((Math.abs(value) * factor).toPrecision(15));
    const rounded = (Math.sign(value) * Math.round(scaled)) / factor;
    // Never hand back -0: it prints as "-0.0".
    return rounded === 0 ? 0 : rounded;
};
/** Storage rounding for a value entered against a KPI of `unit`. */
export const roundForUnit = (unit, value) => roundTo(value, STORAGE_DECIMALS[unit]);
/** Display rounding — one decimal, every unit. */
export const roundToOneDecimal = (value) => roundTo(value, DISPLAY_DECIMALS);
const withThousandsSeparators = (value) => {
    const [integerPart, fractionPart] = Math.abs(value).toFixed(DISPLAY_DECIMALS).split('.');
    const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${value < 0 ? '-' : ''}${grouped}.${fractionPart}`;
};
/**
 * Display text for a value: `1,450.0`, `12.5%`, `JOD 1,450.0`; a blank is `—`.
 * A Currency value without a currency (which the KPI rules never allow) is shown without a code rather than guessed.
 */
export const formatValue = (unit, value, currency = null) => {
    if (value === null || !Number.isFinite(value)) {
        return BLANK_DISPLAY;
    }
    const number = withThousandsSeparators(roundToOneDecimal(value));
    if (unit === 'PERCENTAGE') {
        return `${number}%`;
    }
    if (unit === 'CURRENCY' && currency !== null) {
        return `${currency} ${number}`;
    }
    return number;
};
/** Plain decimal notation only: an optional sign, digits, an optional fraction. No exponents, no hex. */
const NUMBER_PATTERN = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;
/**
 * Parses what a user typed (or pasted) into a tracker cell. Thousands separators and spaces are ignored, a
 * trailing `%` is accepted on Percentage KPIs, and the storage rounding of the unit is applied. An empty cell is
 * a blank — never zero.
 */
export const parseInputForUnit = (unit, raw) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
        return { ok: true, kind: 'blank' };
    }
    if (!isNumericUnit(unit)) {
        return { ok: true, kind: 'text', text: trimmed };
    }
    const withoutPercent = unit === 'PERCENTAGE' && trimmed.endsWith('%') ? trimmed.slice(0, -1) : trimmed;
    const normalized = withoutPercent.replace(/[,\s]/g, '');
    const value = Number(normalized);
    // A 400-digit paste parses to Infinity; that is not a number anyone meant either.
    if (!NUMBER_PATTERN.test(normalized) || !Number.isFinite(value)) {
        return { ok: false, error: 'NOT_A_NUMBER' };
    }
    return { ok: true, kind: 'number', value: roundForUnit(unit, value) };
};
//# sourceMappingURL=units.js.map